import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, onValue, push, set, onDisconnect } from 'firebase/database';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Hello! How can we assist you with your immigration needs?' }
  ]);
  const [step, setStep] = useState(0);
  const [input, setInput] = useState('');
  const [userInfo, setUserInfo] = useState({
    email: '',
    phone: '',
    query: '',
    hasContactInfo: false,
    issueResolved: false
  });
  const [isAdminOnline, setIsAdminOnline] = useState(false);
  const [waitingForAdmin, setWaitingForAdmin] = useState(false);
  const [waitingTime, setWaitingTime] = useState(0);

  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');
  
  // Generate stable guest ID if not logged in (only once)
  const guestUserId = useMemo(() => {
    if (!userId) {
      let guestId = localStorage.getItem('guestUserId');
      if (!guestId) {
        guestId = 'guest_' + Date.now() + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('guestUserId', guestId);
      }
      return guestId;
    }
    return userId;
  }, [userId]);

  const currentUserId = userId || guestUserId;
  const currentUserName = userName || 'Guest User';

  useEffect(() => {
    if (currentUserId) {
      const database = getDatabase();
      const onlineRef = ref(database, `onlineUsers/${currentUserId}`);
      set(onlineRef, {
        name: currentUserName,
        lastSeen: new Date().toISOString()
      });
      onDisconnect(onlineRef).remove();

      // Check admin online status
      const adminOnlineRef = ref(database, 'adminOnline');
      onValue(adminOnlineRef, (snapshot) => {
        const adminOnline = snapshot.val();
        setIsAdminOnline(!!adminOnline);
      });

      // Load chat messages in real-time
      const chatRef = ref(database, `chats/${currentUserId}`);
      onValue(chatRef, (snapshot) => {
        const chatData = snapshot.val();
        if (chatData) {
          const messageList = Object.entries(chatData).map(([id, data]) => ({ id, ...data }));
          setMessages(messageList.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
          
          // Check if user already has contact info from previous messages
          const hasEmail = messageList.some(msg => msg.message && msg.message.includes('Email:'));
          const hasPhone = messageList.some(msg => msg.message && msg.message.includes('Phone:'));
          if (hasEmail || hasPhone) {
            setUserInfo(prev => ({ ...prev, hasContactInfo: true }));
          }
        }
      });
      
      // Check if user has existing queries with contact info
      const queriesRef = ref(database, 'queries');
      onValue(queriesRef, (snapshot) => {
        const queries = snapshot.val();
        if (queries) {
          const userQueries = Object.entries(queries).filter(([id, data]) => data.userId === currentUserId);
          if (userQueries.length > 0) {
            const latestQuery = userQueries[userQueries.length - 1][1];
            if (latestQuery.email || latestQuery.phone) {
              setUserInfo(prev => ({ 
                ...prev, 
                hasContactInfo: true,
                email: latestQuery.email || '',
                phone: latestQuery.phone || ''
              }));
            }
          }
        }
      });
    }
  }, [currentUserId, currentUserName]);

  // Start waiting timer when admin is offline
  useEffect(() => {
    if (step >= 4 && !isAdminOnline) {
      setWaitingForAdmin(true);
      const timer = setInterval(() => {
        setWaitingTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setWaitingForAdmin(false);
      setWaitingTime(0);
    }
  }, [step, isAdminOnline]);

  // Show unavailable message after 2-3 minutes
  useEffect(() => {
    if (waitingTime >= 180 && !isAdminOnline && step >= 4) {
      const unavailableMessage = {
        sender: 'bot',
        text: 'Support team is not available now. We will reach you soon via email/phone. Thank you for your patience!'
      };
      setMessages(prev => {
        if (!prev.some(msg => msg.text === unavailableMessage.text)) {
          return [...prev, unavailableMessage];
        }
        return prev;
      });
    }
  }, [waitingTime, isAdminOnline, step]);

  const handleSubmit = async () => {
    if (!input.trim()) return;

    const userMessage = input;

    // Send message to Firebase
    try {
      const database = getDatabase();
      await push(ref(database, `chats/${currentUserId}`), {
        senderId: currentUserId,
        senderName: currentUserName,
        message: userMessage,
        timestamp: new Date().toISOString(),
        isAdmin: false
      });
    } catch (error) {
      console.error('Failed to send message:', error);
    }

    setInput('');

    // Handle chatbot flow for initial steps
    if (step < 7) {
      setTimeout(() => {
        let botResponse = '';
        let nextStep = step;

        switch (step) {
          case 0:
            setUserInfo(prev => ({ ...prev, initialMessage: userMessage }));
            // Skip contact info if already collected
            if (userInfo.hasContactInfo) {
              botResponse = 'Perfect! Please describe your immigration query in detail:';
              nextStep = 3;
            } else {
              botResponse = 'Thank you! Could you please provide your email address?';
              nextStep = 1;
            }
            break;
          case 1:
            if (validateEmail(userMessage)) {
              setUserInfo(prev => ({ ...prev, email: userMessage }));
              botResponse = 'Great! Now, could you please provide your phone number?';
              nextStep = 2;
            } else {
              botResponse = 'Please enter a valid email address. Try again:';
              nextStep = 1;
            }
            break;
          case 2:
            if (validatePhone(userMessage)) {
              setUserInfo(prev => ({ ...prev, phone: userMessage, hasContactInfo: true }));
              botResponse = 'Perfect! Please describe your immigration query in detail:';
              nextStep = 3;
            } else {
              botResponse = 'Please enter a valid phone number. Try again:';
              nextStep = 2;
            }
            break;
          case 3:
            setUserInfo(prev => ({ ...prev, query: userMessage }));
            botResponse = isAdminOnline ? 'Thank you! An admin is online and will join shortly.' : 'Thank you! Your information has been collected. An admin will join the chat shortly to assist you.';
            nextStep = 4;
            submitToAdmin(currentUserId, currentUserName);
            break;
          case 4:
            // After admin chat, ask if issue is resolved
            botResponse = 'Has your query been resolved? (Yes/No)';
            nextStep = 5;
            break;
          case 5:
            const isResolved = userMessage.toLowerCase().includes('yes');
            setUserInfo(prev => ({ ...prev, issueResolved: isResolved }));
            if (isResolved) {
              botResponse = 'Great! Is there anything else we can help you with?';
              nextStep = 6;
            } else {
              botResponse = 'I understand. Let me connect you with an admin for further assistance.';
              nextStep = 4;
              // Re-submit query as unresolved
              submitToAdmin(currentUserId, currentUserName);
            }
            break;
          case 6:
            if (userMessage.toLowerCase().includes('yes') || userMessage.toLowerCase().includes('help')) {
              botResponse = 'Please describe your new query:';
              nextStep = 3;
            } else {
              botResponse = 'Thank you for using Travel Immigration support. Have a great day!';
              nextStep = 7;
            }
            break;
          default:
            botResponse = 'Thank you for your patience.';
        }

        // Add bot message locally (not to Firebase)
        setMessages(prev => [...prev, { sender: 'bot', text: botResponse }]);
        setStep(nextStep);
      }, 500);
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^[0-9]{10,15}$/.test(phone.replace(/[\s-]/g, ''));

  const submitToAdmin = async (tempUserId, tempUserName) => {
    try {
      const database = getDatabase();

      await push(ref(database, 'queries'), {
        userId: tempUserId,
        userName: tempUserName,
        email: userInfo.email,
        phone: userInfo.phone,
        initialMessage: userInfo.initialMessage,
        query: userInfo.query,
        status: 'pending',
        createdAt: new Date().toISOString(),
        awaitingAdmin: true
      });

      await push(ref(database, `chats/${tempUserId}`), {
        senderId: 'system',
        senderName: 'System',
        message: `New user query: ${userInfo.query}\nEmail: ${userInfo.email}\nPhone: ${userInfo.phone}`,
        timestamp: new Date().toISOString(),
        isAdmin: false,
        isSystem: true
      });
    } catch (error) {
      console.error('Failed to submit to admin:', error);
    }
  };

  if (!isOpen) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9999
      }}>
        <button
          onClick={() => setIsOpen(true)}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#3B82F6',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            fontSize: '24px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          💬
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      width: '350px',
      height: '500px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#3B82F6',
        color: 'white',
        padding: '16px',
        borderRadius: '12px 12px 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 'bold' }}>Travel Immigration Assistant</span>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: isAdminOnline ? '#10B981' : '#EF4444'
          }}></div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            cursor: 'pointer'
          }}
        >
          ×
        </button>
      </div>

      {/* Admin Status Message */}
      {step >= 4 && (
        <div style={{
          padding: '8px 16px',
          backgroundColor: isAdminOnline ? '#D1FAE5' : '#FEE2E2',
          color: isAdminOnline ? '#065F46' : '#991B1B',
          fontSize: '12px',
          textAlign: 'center',
          borderBottom: '1px solid #E5E7EB'
        }}>
          {isAdminOnline ? '🟢 Admin is online - Live chat available' : '🔴 Admin is offline - Leave a message'}
          {waitingForAdmin && !isAdminOnline && (
            <div style={{ marginTop: '4px', fontSize: '11px' }}>
              Waiting time: {Math.floor(waitingTime / 60)}:{(waitingTime % 60).toString().padStart(2, '0')}
              {waitingTime >= 180 && ' - Support team is not available now. We will reach you soon.'}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: '12px',
              backgroundColor: msg.sender === 'bot' ? '#3B82F6' : 
                              msg.isAdmin ? '#10B981' : 
                              msg.isSystem ? '#F59E0B' : '#E5E7EB',
              color: msg.sender === 'bot' ? 'white' : 
                     msg.isAdmin ? 'white' : 
                     msg.isSystem ? 'white' : '#1F2937',
              alignSelf: msg.sender === 'bot' ? 'flex-start' : 
                       msg.isAdmin ? 'flex-start' : 
                       msg.isSystem ? 'center' : 'flex-end',
              fontSize: '14px'
            }}
          >
            {msg.isAdmin && <div style={{ fontSize: '11px', marginBottom: '4px', opacity: 0.8 }}>Admin</div>}
            {msg.isSystem && <div style={{ fontSize: '11px', marginBottom: '4px', opacity: 0.8 }}>System</div>}
            {msg.text || msg.message}
          </div>
        ))}
      </div>

      {/* Progress Indicator */}
      {step < 4 && (
        <div style={{ padding: '0 16px 8px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[0, 1, 2, 3].map((s) => (
              <div
                key={s}
                style={{
                  flex: 1,
                  height: '3px',
                  backgroundColor: step > s ? '#10B981' : step === s ? '#3B82F6' : '#E5E7EB',
                  borderRadius: '2px'
                }}
              />
            ))}
          </div>
          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
            {step === 0 && 'Tell us how we can help'}
            {step === 1 && 'Provide your email'}
            {step === 2 && 'Provide your phone number'}
            {step === 3 && 'Describe your query'}
          </div>
        </div>
      )}

      {/* Input */}
      <div style={{ padding: '16px', borderTop: '1px solid #E5E7EB' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={step >= 4 ? "Continue chatting with admin..." : "Type your message..."}
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            disabled={step >= 7}
            style={{
              flex: 1,
              padding: '10px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '14px'
            }}
          />
          <button
            onClick={handleSubmit}
            disabled={step >= 7}
            style={{
              padding: '10px 16px',
              backgroundColor: step >= 7 ? '#9CA3AF' : '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: step >= 7 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatbotWidget;
